import React, { useEffect, useRef, useState } from 'react';
import Nav from "../../components/Nav";
import Body from "../../components/Body";
import { useNavigate, useParams } from "react-router-dom";
import { InputText } from "primereact/inputtext";
import { Dropdown } from 'primereact/dropdown';
import './AccountForm.css';
import { Button } from "primereact/button";
import { useAccount, useCreateAccount, useUpdateAccount } from '../../state/accountState';
import { Toast } from 'primereact/toast';
import { OverlayPanel } from 'primereact/overlaypanel';
import useHasRole from "../../hooks/validRolesHook";
import NotFound from "../Global/404NotFound";
import Loading from "../Global/loading";
import { getTenantPath } from '../Global/TenantPath';

interface State {
    name: string;
    code: string;
}

// ---------------------------------------------------------------------------
// Account Name rules
// Length: 3-15 | Letters, numbers, spaces, hyphens only | No apostrophes
// No consecutive hyphens | Uniqueness is checked by the save API (409)
// ---------------------------------------------------------------------------
const MIN_LEN = 3;
const MAX_LEN = 15; // confirm with your team: the sample "Greenhome Mortgage Lending" is 26 chars

export function validateAccountName(name?: string): string {
    const v = (name ?? '').trim();
    if (!v) return 'Account Name is required.';
    if (v.length < MIN_LEN || v.length > MAX_LEN)
        return `Account Name must be ${MIN_LEN}–${MAX_LEN} characters.`;
    if (/['’]/.test(v)) return 'Apostrophes are not allowed.';
    if (!/^[A-Za-z0-9 -]+$/.test(v))
        return 'Only letters, numbers, spaces and hyphens are allowed.';
    if (/--/.test(v)) return 'Consecutive hyphens are not allowed.';
    return '';
}

const DUPLICATE_MESSAGE = 'This Account Name already exists. Account names must be unique across QC Pro.';

export default function AccountForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = id != undefined;
    const { accountStatus, accountData, accountError } = useAccount(id);
    const [account, setAccount] = useState(accountData);
    // Set up isvalid for validation checking
    const [isValid, setIsValid] = useState<any>({
        AccountName: true,
        Street: true,
        City: true,
        State: true,
        PostalCode: true,
        Country: true,
        RecordStatus: true,
    });
    // Message shown under the Account Name input
    const [accountNameError, setAccountNameError] = useState('');
    const toast = useRef<Toast>(null);
    const op = useRef<OverlayPanel>(null);
    const _opMsg = useRef("");
    const { mutate: createAccount, isSuccess: isCreateSuccess } = useCreateAccount();
    const { mutate: updateAccount, isSuccess: isUpdateSuccess } = useUpdateAccount();

    const states: State[] = [
        { name: 'Alabama', code: 'AL' },
        { name: 'Alaska', code: 'AK' },
        { name: 'Arizona', code: 'AZ' },
        { name: 'Arkansas', code: 'AR' },
        { name: 'California', code: 'CA' },
        { name: 'Colorado', code: 'CO' },
        { name: 'Connecticut', code: 'CT' },
        { name: 'Delaware', code: 'DE' },
        { name: 'District of Columbia', code: 'DC' },
        { name: 'Florida', code: 'FL' },
        { name: 'Georgia', code: 'GA' },
        { name: 'Guam', code: 'GU' },
        { name: 'Hawaii', code: 'HI' },
        { name: 'Idaho', code: 'ID' },
        { name: 'Illinois', code: 'IL' },
        { name: 'Indiana', code: 'IN' },
        { name: 'Iowa', code: 'IA' },
        { name: 'Kansas', code: 'KS' },
        { name: 'Kentucky', code: 'KY' },
        { name: 'Louisiana', code: 'LA' },
        { name: 'Maine', code: 'ME' },
        { name: 'Maryland', code: 'MD' },
        { name: 'Massachusetts', code: 'MA' },
        { name: 'Michigan', code: 'MI' },
        { name: 'Minnesota', code: 'MN' },
        { name: 'Mississippi', code: 'MS' },
        { name: 'Missouri', code: 'MO' },
        { name: 'Montana', code: 'MT' },
        { name: 'Nebraska', code: 'NE' },
        { name: 'Nevada', code: 'NV' },
        { name: 'New Hampshire', code: 'NH' },
        { name: 'New Jersey', code: 'NJ' },
        { name: 'New Mexico', code: 'NM' },
        { name: 'New York', code: 'NY' },
        { name: 'North Carolina', code: 'NC' },
        { name: 'North Dakota', code: 'ND' },
        { name: 'Northern Mariana Islands', code: 'MP' },
        { name: 'Ohio', code: 'OH' },
        { name: 'Oklahoma', code: 'OK' },
        { name: 'Oregon', code: 'OR' },
        { name: 'Pennsylvania', code: 'PA' },
        { name: 'Puerto Rico', code: 'PR' },
        { name: 'Rhode Island', code: 'RI' },
        { name: 'South Carolina', code: 'SC' },
        { name: 'South Dakota', code: 'SD' },
        { name: 'Tennessee', code: 'TN' },
        { name: 'Texas', code: 'TX' },
        { name: 'Trust Territories', code: 'TT' },
        { name: 'Utah', code: 'UT' },
        { name: 'Vermont', code: 'VT' },
        { name: 'Virginia', code: 'VA' },
        { name: 'Virgin Islands', code: 'VI' },
        { name: 'Washington', code: 'WA' },
        { name: 'West Virginia', code: 'WV' },
        { name: 'Wisconsin', code: 'WI' },
        { name: 'Wyoming', code: 'WY' }
    ];

    const [logoPreview, setLogoPreview] = useState<string | null>(
        account?.LogoImage ? `data:image/png;base64,${account.LogoImage}` : null
    );

    useEffect(() => {
        setAccount(accountData);
        setLogoPreview(accountData?.LogoImage ? `data:image/png;base64,${accountData.LogoImage}` : null);
    }, [accountData]);

    // On edit, only enforce the format rules when the name is actually being changed,
    // so older accounts with legacy names can still be edited (address, logo, etc.).
    const isNameChanged = (name?: string): boolean => {
        if (!isEdit) return true;
        const original = (accountData?.AccountName ?? '').trim().toLowerCase();
        return (name ?? '').trim().toLowerCase() !== original;
    };

    // Function to check if anything is invalid
    function checkValid(e: any): boolean {
        let hasError = false;
        if (e === null || e === undefined) {
            hasError = true;
            setAccountNameError('Account Name is required.');
            setIsValid({
                AccountName: false,
                Street: false,
                City: false,
                State: false,
                PostalCode: false,
                Country: false,
                RecordStatus: false,
            })
        }
        else {
            // Account Name: required always, full format rules for new accounts or when renamed
            const nameValue = (e.AccountName ?? '').trim();
            let nameErr = '';
            if (!nameValue) {
                nameErr = 'Account Name is required.';
            } else if (isNameChanged(nameValue)) {
                nameErr = validateAccountName(nameValue);
            }
            if (nameErr) {
                setAccountNameError(nameErr);
                setIsValid((errors: any) => ({ ...errors, AccountName: false }));
                hasError = true;
            } else {
                setAccountNameError('');
                setIsValid((errors: any) => ({ ...errors, AccountName: true }));
            }

            if (e.Street === null || e.Street === "" || e.Street === undefined) {
                handleErrors("Street", e.Street);
                hasError = true;
            }
            if (e.City === null || e.City === "" || e.City === undefined) {
                handleErrors("City", e.City);
                hasError = true;
            }
            if (e.State === null || e.State === "" || e.State === undefined) {
                handleErrors("State", e.State);
                hasError = true;
            }
            if (e.PostalCode === null || e.PostalCode === "" || e.PostalCode === undefined) {
                handleErrors("PostalCode", e.PostalCode);
                hasError = true;
            }
            if (e.Country === null || e.Country === "" || e.Country === undefined) {
                handleErrors("Country", e.Country);
                hasError = true;
            }
            if (e.RecordStatus === null || e.RecordStatus === "" || e.RecordStatus === undefined) {
                handleErrors("RecordStatus", e.RecordStatus);
                hasError = true;
            }
        }
        return hasError;
    }

    const checkSave = (e: any) => {
        let hasError = checkValid(e);
        // If check valid returns true, then don't save and show an error
        if (hasError) {
            toast.current?.show({ severity: 'warn', summary: 'Error', detail: 'One or more required items were incomplete.', life: 10000 });
            return
        }
        // Else, go ahead and save
        handleSave();
    }

    // Shown when the save API says the Account Name already exists
    const showDuplicateError = () => {
        setAccountNameError(DUPLICATE_MESSAGE);
        setIsValid((errors: any) => ({ ...errors, AccountName: false }));
        toast.current?.show({ severity: 'error', summary: 'Duplicate Name', detail: DUPLICATE_MESSAGE, life: 10000 });
    };

    const handleSave = () => {
    const payload = { ...account, AccountName: (account?.AccountName ?? '').trim() };

    const options = {
        onError: (error: any) => {
            console.log('save error:', error); // check the shape in the browser console

            const status =
                error?.response?.status ??          // axios
                error?.status ??                    // custom error from fetch wrapper
                error?.response?.data?.statusCode;  // statusCode in the body, if you added it

            const message = String(
                error?.response?.data?.message ??
                error?.response?.data ??
                error?.message ??
                ''
            );

            if (status === 409 || /already exists|must be unique/i.test(message)) {
                showDuplicateError();
                return;
            }

            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'This Account Name already exists. Account names must be unique across QC Pro.',
                life: 10000
            });
        },
    };

    if (isEdit) {
        updateAccount(payload, options);
    } else {
        createAccount(payload, options);
    }
};

    useEffect(() => {
        if (isCreateSuccess || isUpdateSuccess) {
            toast.current?.show({ severity: 'success', summary: 'Confirmed', detail: 'Account successfully saved.', life: 3000 });
            //navigate('/accounts');
            //The below line i'm adding the getTenantPath to make sure that the accountName is prefixed to the url when navigating to accounts page after saving an account. in New CR Multi Teant
            navigate(getTenantPath('accounts'));
            window.location.reload(); // forces AccountResolver to re-fetch and re-populate sessionStorage
        }
    }, [isCreateSuccess, isUpdateSuccess, navigate]);

    // NOTE: the old useEffect that watched isCreateError / isUpdateError was removed.
    // Errors are now handled in the onError option inside handleSave.

    const handleChange = (field: string, value: string) => {
        setAccount({ ...account, [field]: value });

        if (field === 'AccountName') {
            // Validate while typing (format rules; uniqueness comes from the save API)
            const err = !value?.trim()
                ? 'Account Name is required.'
                : isNameChanged(value) ? validateAccountName(value) : '';
            setAccountNameError(err);
            setIsValid((errors: any) => ({ ...errors, AccountName: !err }));
            return;
        }

        handleErrors(field, value);
    };

    const handleErrors = (field: string, value: string) => {
        if (value === undefined || value === null || value === '') {
            setIsValid((errors: any) => ({ ...errors, [field]: false }))
        }
        else {
            setIsValid((errors: any) => ({ ...errors, [field]: true }))
        }
    }

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.type !== 'image/png' || !file.name.toLowerCase().endsWith('.png')) {
            toast.current?.show({ severity: 'warn', summary: 'Invalid file', detail: 'Only .png files are allowed.', life: 5000 });
            e.target.value = '';
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;           // "data:image/png;base64,AAAA..."
            const base64Only = result.split(',')[1];           // strip the prefix
            setAccount((prev: any) => ({ ...prev, LogoImage: base64Only }));
            setLogoPreview(result);                             // keep prefix for <img> preview
        };
        reader.readAsDataURL(file);
    };

    // Security to check if the user has any of the allowed roles.
    // NOTE: Keep below any react.js "use" items (i.e., useHook, useEffect, useState, useMemo, etc.)
    let {hasRoles, isLoading} = useHasRole(['System Administrator','Master Administrator']);
    // const { hasRoles: isSecurityAdmin } = useHasRole(['Security Administrator']);
    if (isLoading) {
        return <Loading />
    }
    else if (!hasRoles) {
        return NotFound();
    }

    return (
        <div className="account-form">
            <Nav />
            <Body>
                <div>
                    <OverlayPanel ref={op}>
                        <div>{_opMsg.current}</div>
                    </OverlayPanel>
                    <Toast ref={toast} position="bottom-center" />
                    <div className="flex justify-content-between">
                        <h1>Account</h1>
                        <Button label="Save" icon="pi pi-save" onClick={() => checkSave(account)} />
                    </div>

                    <div className="form">
                        <div className="form-column gap-5">
                            <span className="p-float-label">
                                <InputText
                                    id="AccountName"
                                    value={account?.AccountName}
                                    onChange={(e) => handleChange('AccountName', e.target.value)}
                                    className={isValid.AccountName === false ? 'p-invalid ' : ''}

                                />
                                <label htmlFor="AccountName">Account Name*</label>
                            </span>
                            {accountNameError && (
                                <small className="p-error" style={{ marginTop: '-1rem' }}>
                                    {accountNameError}
                                </small>
                            )}
                            <span className="p-float-label">
                                <InputText
                                    id="Street"
                                    value={account?.Street}
                                    onChange={(e) => handleChange('Street', e.target.value)}
                                    className={isValid.Street === false ? 'p-invalid ' : ''}

                                />
                                <label htmlFor="Street">Street*</label>
                            </span>
                            <span className="p-float-label">
                                <InputText
                                    id="City"
                                    value={account?.City}
                                    onChange={(e) => handleChange('City', e.target.value)}
                                    className={isValid.City === false ? 'p-invalid ' : ''}
                                />
                                <label htmlFor="City">City*</label>
                            </span>
                            <span className="p-label">
                                <label htmlFor="State">State*</label>
                                <Dropdown
                                    value={account?.State}
                                    onChange={(e) => handleChange('State', e.value.code)}
                                    options={states}
                                    optionLabel="name"
                                    placeholder={(account?.State !== null) && (account?.State !== undefined) && (account?.State !== "") ?
                                        account?.State
                                        :
                                        "Please Select"}
                                    filter
                                    className={isValid.State === false ? 'w-full p-invalid' : 'w-full'}
                                />
                            </span>
                            <span className="p-float-label">
                                <InputText
                                    id="PostalCode"
                                    value={account?.PostalCode}
                                    onChange={(e) => handleChange('PostalCode', e.target.value)}
                                    className={isValid.PostalCode === false ? 'p-invalid ' : ''}
                                />
                                <label htmlFor="PostalCode">Postal Code*</label>
                            </span>
                            <span className="p-float-label">
                                <InputText
                                    id="Country"
                                    value={account?.Country}
                                    onChange={(e) => handleChange('Country', e.target.value)}
                                    className={isValid.Country === false ? 'p-invalid ' : ''}

                                />
                                <label htmlFor="Country">Country*</label>
                            </span>
                        </div>

                        <div className="form-column gap-5">
                            <span className="p-float-label">
                                <Dropdown
                                    value={account?.RecordStatus}
                                    onChange={(e) => handleChange('RecordStatus', e.target.value)}
                                    options={["Active", "Inactive"]}
                                    className={isValid.RecordStatus === false ? 'w-full p-invalid' : 'w-full'}
                                    id="recordStatus"
                                    // disabled={!isSecurityAdmin}
                                />
                                <label htmlFor="recordStatus">Record Status*</label>
                            </span>

                            <span className="p-float-label">
                                <InputText
                                    id="ShippingStreet"
                                    value={account?.ShippingStreet}
                                    onChange={(e) => handleChange('ShippingStreet', e.target.value)}
                                />
                                <label htmlFor="ShippingStreet">Shipping Street</label>
                            </span>
                            <span className="p-float-label">
                                <InputText
                                    id="ShippingCity"
                                    value={account?.ShippingCity}
                                    onChange={(e) => handleChange('ShippingCity', e.target.value)}
                                />
                                <label htmlFor="ShippingCity">Shipping City</label>
                            </span>
                            <span className="p-label">
                                <label htmlFor="ShippingState">Shipping State</label>
                                <Dropdown
                                    value={account?.ShippingState}
                                    onChange={(e) => handleChange('ShippingState', e.value.code)}
                                    options={states}
                                    optionLabel="name"
                                    placeholder={(account?.ShippingState !== null) && (account?.ShippingState !== undefined) && (account?.ShippingState !== "") ?
                                        account?.ShippingState
                                        :
                                        "Please Select"}
                                    filter
                                    className="w-full"
                                />
                            </span>
                            <span className="p-float-label">
                                <InputText
                                    id="ShippingPostalCode"
                                    value={account?.ShippingPostalCode}
                                    onChange={(e) => handleChange('ShippingPostalCode', e.target.value)}
                                />
                                <label htmlFor="ShippingPostalCode">Shipping Postal Code</label>
                            </span>
                            <span className="p-float-label">
                                <InputText
                                    id="ShippingCountry"
                                    value={account?.ShippingCountry}
                                    onChange={(e) => handleChange('ShippingCountry', e.target.value)}
                                />
                                <label htmlFor="ShippingCountry">Shipping Country</label>
                            </span>
                            <div className="flex align-items-center gap-3 mb-4">
                                {logoPreview && (
                                    <img src={logoPreview} alt="Account logo" style={{ height: '56px', border: '1px solid #ddd', borderRadius: 4 }} />
                                )}
                                
                                    <span>
                                        <label htmlFor="logoUpload" className="p-button p-button-outlined" style={{ cursor: 'pointer' }}>
                                            <i className="pi pi-upload mr-2" />
                                            {logoPreview ? 'Replace Logo (.png)' : 'Upload Logo (.png)'}
                                        </label>
                                        <input
                                            id="logoUpload"
                                            type="file"
                                            accept=".png,image/png"
                                            onChange={handleLogoChange}
                                            style={{ display: 'none' }}
                                        />
                                    </span>
                              
                            </div>
                        </div>
                    </div>
                </div>
            </Body>
        </div>
    );
}